import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Typography, Card, CardContent, Box, Chip, Button, Grid,
  Alert, Divider
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import billingApi from '../services/billingApi';
import PlanCard from '../components/PlanCard';
import SubscriptionCardSkeleton from '../components/SubscriptionCardSkeleton';
import { formatDateShort as formatDate, subscriptionStatusColor } from '../utils/formatters';

export default function SubscriptionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [changingPlan, setChangingPlan] = useState(false);

  useEffect(() => {
    loadData();
  }, [id]);

  async function loadData() {
    try {
      const [subData, plansData] = await Promise.all([
        billingApi.get(`/api/subscriptions/${id}`),
        billingApi.get('/api/plans')
      ]);
      setSubscription(subData.subscription);

      // Filter plans for the same product
      const productPlans = plansData.plans.filter(
        p => p.productId === subData.subscription.productId && p.planId !== subData.subscription.planId
      );
      setAvailablePlans(productPlans);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleChangePlan(plan) {
    setChangingPlan(true);
    setError('');
    try {
      await billingApi.post(`/api/subscriptions/${id}/change-plan`, { newPlanId: plan.id });
      loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setChangingPlan(false);
    }
  }

  if (loading) {
    // Static Back button renders during loading too — only the card contents
    // swap when the subscription arrives (standard rule 1, no layout jump).
    return (
      <Container maxWidth="md" sx={{ py: 4 }} aria-busy="true">
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/subscriptions')} sx={{ mb: 2 }}>
          Back to Subscriptions
        </Button>
        <SubscriptionCardSkeleton />
        <Box component="span" role="status" sx={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}>
          Loading subscription…
        </Box>
      </Container>
    );
  }

  if (!subscription) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">Subscription not found</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/subscriptions')} sx={{ mb: 2 }}>
        Back to Subscriptions
      </Button>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h5">
              {subscription.product_name} — {subscription.plan_name}
            </Typography>
            <Chip
              label={
                subscription.status === 'trialing' ? 'Free Trial' :
                subscription.status === 'canceled' ? 'Canceled' :
                subscription.status === 'expired' ? 'Expired' :
                subscription.status.replace('_', ' ')
              }
              color={subscriptionStatusColor(subscription.status)}
            />
          </Box>

          {/* Canceled banner */}
          {subscription.status === 'canceled' && (
            <Alert severity="error" icon={false} sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight={600}>This subscription has been canceled</Typography>
              <Typography variant="caption" color="text.secondary">
                {subscription.canceled_at ? `Canceled on ${formatDate(subscription.canceled_at)}.` : ''}
                {subscription.ended_at ? ` Access ended ${formatDate(subscription.ended_at)}.` : ''}
              </Typography>
            </Alert>
          )}

          {/* Expired banner */}
          {subscription.status === 'expired' && (
            <Alert severity="warning" icon={false} sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight={600}>
                This subscription has expired
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Renew your subscription to regain access.
              </Typography>
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Status</Typography>
              <Typography>{subscription.status}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Price</Typography>
              <Typography>
                ${(subscription.price_cents / 100).toFixed(2)}/{subscription.interval_type === 'year' ? 'yr' : 'mo'}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Current Period Start</Typography>
              <Typography>{formatDate(subscription.current_period_start)}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Current Period End</Typography>
              <Typography>{formatDate(subscription.current_period_end)}</Typography>
            </Grid>
            {subscription.trial_end && (
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Trial End (historical)</Typography>
                <Typography>{formatDate(subscription.trial_end)}</Typography>
              </Grid>
            )}
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Created</Typography>
              <Typography>{formatDate(subscription.created_at)}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Amount paid</Typography>
              <Typography>$0.00</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {['active', 'trialing'].includes(subscription.status) && availablePlans.length > 0 && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" gutterBottom>Change Plan</Typography>
          <Grid container spacing={3}>
            {availablePlans.map(plan => (
              <Grid item xs={12} sm={6} key={plan.id}>
                <PlanCard
                  plan={plan}
                  onSelect={handleChangePlan}
                  disabled={changingPlan}
                />
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Container>
  );
}
